/**
 * Role-templates gallery (finalization WS2). Templates are versioned starting
 * points, never grant targets: applying one snapshots a tenant role instance
 * (which then appears in the matrix), and instances never auto-update — a new
 * template version surfaces as an indicator + diff, with an explicit Apply.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowUpCircle, Check, Star } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useApplyRoleTemplate, useArchiveTemplate, useListRoleTemplates, usePublishTemplate, useTemplateDiff } from '../Access/hooks';
import { actionLabel, viewModeClass, viewModeLabel } from './vocabulary';
const STATUS_CLASS = {
    published: 'bg-emerald-100 text-emerald-800',
    draft: 'bg-amber-100 text-amber-800',
    archived: 'bg-muted text-muted-foreground'
};
/** One family = one card; the newest version of each status wins the chip. */ function groupByFamily(templates) {
    const fams = new Map();
    for (const t of templates){
        const list = fams.get(t.familyId) ?? [];
        list.push(t);
        fams.set(t.familyId, list);
    }
    return [
        ...fams.values()
    ].map((list)=>list.sort((a, b)=>b.version - a.version));
}
function DiffPanel({ familyId, fromVersion, toVersion }) {
    const { data: diff, isLoading } = useTemplateDiff(familyId, fromVersion, toVersion);
    if (isLoading) return /*#__PURE__*/ _jsx("p", {
        className: "text-xs text-muted-foreground",
        children: "Comparing…"
    });
    if (!diff) return null;
    const line = (label, items, cls)=>items.length > 0 && /*#__PURE__*/ _jsxs("p", {
            className: `text-xs ${cls}`,
            children: [
                label,
                ": ",
                items.map(actionLabel).join(', ')
            ]
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0.5 rounded-md bg-muted/40 p-2",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-[11px] font-semibold uppercase tracking-wide text-muted-foreground",
                children: "What changes if you apply the update"
            }),
            line('Adds', diff.addedActions, 'text-emerald-700'),
            line('Removes', diff.removedActions, 'text-red-700'),
            line('Now sees', diff.addedPacks, 'text-emerald-700'),
            line('No longer sees', diff.removedPacks, 'text-red-700'),
            diff.viewModeFrom !== diff.viewModeTo && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs",
                children: [
                    "Visibility: ",
                    viewModeLabel(diff.viewModeFrom),
                    " → ",
                    viewModeLabel(diff.viewModeTo)
                ]
            }),
            diff.addedActions.length === 0 && diff.removedActions.length === 0 && diff.addedPacks.length === 0 && diff.removedPacks.length === 0 && diff.viewModeFrom === diff.viewModeTo && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Name/description changes only."
            })
        ]
    });
}
export function TemplatesGallery({ bundles }) {
    const { data: templates, isLoading } = useListRoleTemplates('');
    const apply = useApplyRoleTemplate();
    const publish = usePublishTemplate();
    const archive = useArchiveTemplate();
    const [diffFor, setDiffFor] = useState(null);
    // Collapsed by default (admin redesign): the matrix above is the page's
    // point; the gallery expands on demand instead of tripling page height.
    const [expanded, setExpanded] = useState(false);
    // Which template family each active role came from — the lineage join.
    const instanceByFamily = useMemo(()=>new Map(bundles.filter((b)=>b.sourceTemplateFamilyId).map((b)=>[
                b.sourceTemplateFamilyId,
                b
            ])), [
        bundles
    ]);
    const families = useMemo(()=>{
        const grouped = groupByFamily(templates ?? []);
        // Curated picks first, then platform, then private.
        return grouped.sort((a, b)=>{
            const ra = Number(a[0].recommended);
            const rb = Number(b[0].recommended);
            if (ra !== rb) return rb - ra;
            return Number(b[0].isPlatform) - Number(a[0].isPlatform);
        });
    }, [
        templates
    ]);
    if (isLoading || families.length === 0) return null;
    const activeCount = families.filter((v)=>instanceByFamily.get(v[0].familyId)).length;
    const upgradeCount = families.filter((versions)=>{
        const latestPublished = versions.find((v)=>v.status === 'published');
        const instance = instanceByFamily.get(versions[0].familyId);
        return instance && latestPublished && latestPublished.version > (instance.sourceTemplateVersion || 0);
    }).length;
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": Testids.adminConsole.templatesGallery,
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                className: "pb-2",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx(CardTitle, {
                                    className: "text-sm",
                                    children: "Role templates"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: [
                                        activeCount,
                                        " of ",
                                        families.length,
                                        " active in your organization",
                                        upgradeCount > 0 ? ` · ${upgradeCount} with a newer version` : '',
                                        " — starting points you can add; roles never change on their own when a template updates."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            size: "sm",
                            "data-testid": Testids.adminConsole.templatesGalleryToggle,
                            onClick: ()=>setExpanded((v)=>!v),
                            children: expanded ? 'Hide templates' : `Browse templates (${families.length})`
                        })
                    ]
                })
            }),
            expanded && /*#__PURE__*/ _jsx(CardContent, {
                children: /*#__PURE__*/ _jsx("div", {
                    className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3",
                    children: families.map((versions)=>{
                        const latestPublished = versions.find((v)=>v.status === 'published');
                        const head = latestPublished ?? versions[0];
                        const instance = instanceByFamily.get(head.familyId);
                        const upgradeAvailable = instance && latestPublished && latestPublished.version > (instance.sourceTemplateVersion || 0);
                        return /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col gap-1.5 rounded-md border p-3",
                            "data-testid": Testids.adminConsole.templateCard,
                            "data-template-key": head.key,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-start justify-between gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "text-sm font-medium",
                                            children: [
                                                head.displayName,
                                                head.recommended && /*#__PURE__*/ _jsx(Star, {
                                                    className: "ml-1 inline h-3.5 w-3.5 fill-amber-400 text-amber-400",
                                                    "aria-label": "recommended"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "flex shrink-0 items-center gap-1",
                                            children: [
                                                head.status === 'draft' && /*#__PURE__*/ _jsx(Badge, {
                                                    variant: "secondary",
                                                    className: STATUS_CLASS.draft,
                                                    children: "draft — publish to use"
                                                }),
                                                head.status === 'archived' && /*#__PURE__*/ _jsx(Badge, {
                                                    variant: "secondary",
                                                    className: STATUS_CLASS.archived,
                                                    children: "archived"
                                                }),
                                                !head.isPlatform && /*#__PURE__*/ _jsx(Badge, {
                                                    variant: "outline",
                                                    children: "yours"
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "line-clamp-2 min-h-8 text-xs text-muted-foreground",
                                    children: head.description
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-[11px] text-muted-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: `mr-1 inline-block h-2 w-2 rounded-full ${viewModeClass(head.defaultViewMode)}`
                                        }),
                                        viewModeLabel(head.defaultViewMode),
                                        " · ",
                                        head.permittedActions.length,
                                        ' ',
                                        "capabilities",
                                        head.dataPacks.length > 0 ? ` · sees ${head.dataPacks.length} data packs` : ''
                                    ]
                                }),
                                instance && /*#__PURE__*/ _jsxs("p", {
                                    className: "flex items-center gap-1 text-xs text-emerald-700",
                                    children: [
                                        /*#__PURE__*/ _jsx(Check, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        "In use as “",
                                        instance.displayName,
                                        "”"
                                    ]
                                }),
                                upgradeAvailable && latestPublished && /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    "data-testid": Testids.adminConsole.templateDiff,
                                    className: "flex items-center gap-1 text-left text-xs font-medium text-amber-700 hover:underline",
                                    onClick: ()=>setDiffFor(diffFor === head.familyId ? null : head.familyId),
                                    children: [
                                        /*#__PURE__*/ _jsx(ArrowUpCircle, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        "Updated version available — see what changes"
                                    ]
                                }),
                                diffFor === head.familyId && instance && latestPublished && /*#__PURE__*/ _jsx(DiffPanel, {
                                    familyId: head.familyId,
                                    fromVersion: instance.sourceTemplateVersion || 1,
                                    toVersion: latestPublished.version
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-auto flex items-center gap-2 pt-1",
                                    children: [
                                        latestPublished && (!instance || upgradeAvailable) && /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: instance ? 'outline' : 'default',
                                            "data-testid": Testids.adminConsole.templateApply,
                                            "data-template-key": head.key,
                                            disabled: apply.isPending,
                                            onClick: ()=>apply.mutate({
                                                    templateId: latestPublished.id,
                                                    overwriteExisting: !!instance
                                                }),
                                            children: instance ? 'Apply the update' : 'Add to organization'
                                        }),
                                        !head.isPlatform && head.status === 'draft' && /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: "outline",
                                            disabled: publish.isPending,
                                            onClick: ()=>publish.mutate(head.id),
                                            children: "Publish"
                                        }),
                                        !head.isPlatform && head.status === 'published' && /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: "ghost",
                                            className: "text-xs text-muted-foreground",
                                            disabled: archive.isPending,
                                            onClick: ()=>archive.mutate(head.id),
                                            children: "Archive"
                                        })
                                    ]
                                })
                            ]
                        }, head.familyId);
                    })
                })
            })
        ]
    });
}
